<template>
	<div v-if="isOpen" class="share-modal-overlay" @click="handleClickOutside">
		<div class="share-modal-content" @click.stop>
			<!-- QR Code -->
			<div class="share-modal-qr">
				<div class="qr-code-container">
					<QRCode :options="qrCodeOptions" />
				</div>
			</div>
			<!-- 標題、頻道代碼與複製連結 -->
			<div class="share-modal-info">
				<div class="share-modal-heading">
					<h2 class="share-modal-title">{{ meetTitle }}</h2>
					<p class="share-modal-code">{{ t('ChannelCode') }} <b>{{ shortID }}</b></p>
				</div>
				<div class="share-modal-actions">
					<ShareLink :text="t('Tips.Icon.ShareLink')" @share="handleCopyLink" />
				</div>
			</div>
		</div>
	</div>
</template>
<script lang="ts">
import { computed, ComputedRef, defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { QRCodeBaseOptions, QRCodeMode } from '@ipevo-rd/service-web-utils'
import { QRCode, ShareLink } from '@shared-components'
import { useInfoStore } from '@shared-store'
import { useI18n } from 'vue-i18n'
import qrLogo from '@shared-assets/images/logos/logo-qr.svg'

export default defineComponent({
	name: 'ShareQRCodeModal',
	components: { QRCode, ShareLink },
	props: {
		/**
		 * 是否開啟分享 QR Code Modal
		 */
		isOpen: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['close'],
	setup(_, { emit }) {
		// 取得短網址ID以及會議標題
		const { shortID, meetTitle } = storeToRefs(useInfoStore())
		// 取得i18n
		const { t } = useI18n()

		/**
		 * 分享的連結
		 * @return {string}
		 */
		const shareUrl = computed(() => `${window.location.origin}/?s=${shortID.value}`)

		/**
		 * QR Code 的選項，logo 的白邊已經畫在 logo-qr.svg 裡
		 * @return {ComputedRef<QRCodeBaseOptions>}
		 */
		const qrCodeOptions: ComputedRef<QRCodeBaseOptions> = computed(() => ({
			content: shareUrl.value,
			mode: QRCodeMode.GenerateImage,
			width: 480,
			logo: {
				src: qrLogo,
				borderWidth: 0,
			},
		}))

		/**
		 * 複製分享連結，成功才顯示提示
		 * @return {Promise<void>}
		 */
		const handleCopyLink = async (): Promise<void> => {
			// 是否複製成功
			let copied = false

			try {
				// 優先用剪貼簿 API
				await navigator.clipboard.writeText(shareUrl.value)
				copied = true
			} catch {
				// 剪貼簿不可用（非 https、權限被拒）退回舊做法
				const textarea = document.createElement('textarea')
				textarea.value = shareUrl.value
				document.body.appendChild(textarea)
				textarea.select()
				copied = document.execCommand('copy')
				textarea.remove()
			}

			// 複製成功才顯示提示
			if (copied) {
				window.$toast?.({ message: t('Toast.LinkCopied') })
			}
		}

		/**
		 * 點擊非 Modal 區域時關閉 Modal
		 * @return {void}
		 */
		const handleClickOutside = (): void => emit('close')

		return {
			t,
			shortID,
			meetTitle,
			qrCodeOptions,
			handleCopyLink,
			handleClickOutside,
		}
	},
})
</script>
<style scoped lang="scss">
@use '@shared-assets/scss/variables' as *;

// 遮罩，QR 與資訊都在上面
.share-modal-overlay {
	position: fixed;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(0, 0, 0, 0.8);
	z-index: 2000;
}

// 桌機：QR 在左，標題與按鈕在右側直排，欄距 80px
.share-modal-content {
	display: flex;
	align-items: center;
	gap: 80px;
}

.share-modal-qr {
	flex-shrink: 0;
}

.qr-code-container {
	width: 240px;
	height: 240px;
	padding: 4px;
	background: #fff;
	border-radius: 16px;
	overflow: hidden;

	:deep(.qr-code-img) {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}

.share-modal-info {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 32px;
	width: 364px;
}

// 標題與編號是同一組資訊，包在一起欄距才不會夾在中間
.share-modal-heading {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
}

.share-modal-title {
	margin: 0;
	color: #fff;
	font-size: 30px;
	font-weight: 600;
	line-height: 40px;
	letter-spacing: -0.1px;
	overflow-wrap: break-word;
}

.share-modal-code {
	margin: 0;
	color: rgba(255, 255, 255, 0.6);
	font-size: 20px;
	font-weight: 400;

	b {
		color: #fff;
		font-weight: 600;
		letter-spacing: 0.06em;
	}
}

// 窄於 900：標題、QR、按鈕直排，共用 240px 欄寬
@media (max-width: #{$breakpoint-md - 1}) {
	.share-modal-content {
		flex-direction: column;
		align-items: center;
		gap: 0;
	}

	// 讓標題與按鈕升級成 content 的直接 flex item，才能各自排序
	.share-modal-info {
		display: contents;
	}

	.share-modal-heading {
		order: 1;
		width: 240px;
		margin-bottom: 32px;
		align-items: center;
		text-align: center;
	}

	.share-modal-title {
		width: 100%;
		font-size: 20px;
		line-height: 24px;
		letter-spacing: 0.3px;
	}

	.share-modal-code {
		font-size: 16px;
	}

	.share-modal-qr {
		order: 2;
		margin-bottom: 24px;
	}

	.share-modal-actions {
		order: 3;
	}
}
</style>
